import Papa from "papaparse";
import type { FabricSpecsModelSnapshot } from "../specsModel/types";
import { readString } from "../specsModel/utils";
import { parseBoolean, stringifyBoolean } from "./fabricsCsvValueUtils";

export const FABRIC_CSV_ROOT_COLUMNS = [
  "name",
  "group",
  "factory",
  "fabricLot",
  "textColor",
  "description",
] as const;

export const FABRIC_CSV_COLOR_COLUMNS = [
  "name",
  "hex",
  "generatorHex",
  "label",
  "fallback",
  "textWhite",
  "techpackColorName",
  "chartName",
  "column",
  "textureUrl",
  "disabled",
  "colorEndOfLife",
  "migrateTo",
  "colorSkuId",
] as const;

const CSV_COLUMNS = [
  "recordType",
  ...FABRIC_CSV_ROOT_COLUMNS.map((column) => `fabric.${column}`),
  ...FABRIC_CSV_COLOR_COLUMNS.map((column) => `color.${column}`),
] as const;
const REQUIRED_COLOR_STRING_COLUMNS = new Set<
  (typeof FABRIC_CSV_COLOR_COLUMNS)[number]
>(["name", "hex", "generatorHex", "label", "fallback"]);

type CsvRecordType = "fabric" | "defaultColor" | "color";
type CsvRow = Record<(typeof CSV_COLUMNS)[number], string>;
type SupportedDelimiter = "," | "\t";

function serializeRow(row: CsvRow) {
  return CSV_COLUMNS.map((column) => row[column] ?? "");
}

function createEmptyRow(recordType: CsvRecordType): CsvRow {
  return CSV_COLUMNS.reduce(
    (row, column) => ({
      ...row,
      [column]: column === "recordType" ? recordType : "",
    }),
    {} as CsvRow
  );
}

function buildColorRow(
  recordType: Extract<CsvRecordType, "defaultColor" | "color">,
  color: Record<string, unknown>
) {
  const row = createEmptyRow(recordType);
  for (const column of FABRIC_CSV_COLOR_COLUMNS) {
    const key = `color.${column}` as const;
    const value = color[column];
    if (
      column === "textWhite" ||
      column === "disabled" ||
      column === "colorEndOfLife"
    ) {
      row[key] = Object.prototype.hasOwnProperty.call(color, column)
        ? stringifyBoolean(Boolean(value))
        : "";
      continue;
    }
    row[key] = typeof value === "string" ? value : "";
  }
  return row;
}

function detectDelimiter(input: string): SupportedDelimiter {
  const firstLine = input.split(/\r?\n/, 1)[0] ?? "";
  return firstLine.includes("\t") ? "\t" : ",";
}

function parseDelimited(input: string, delimiter: SupportedDelimiter) {
  const { data, errors } = Papa.parse<string[]>(input, {
    delimiter,
    skipEmptyLines: false,
  });

  if (errors.length > 0) {
    throw new Error(errors[0].message);
  }

  return data.filter((row) => row.some((value) => value !== ""));
}

function normalizeRowWidth(values: string[]) {
  let normalizedValues = [...values];
  while (
    normalizedValues.length > 0 &&
    normalizedValues[normalizedValues.length - 1] === ""
  ) {
    normalizedValues.pop();
  }

  if (normalizedValues.length > CSV_COLUMNS.length) {
    throw new Error("CSV row does not match the expected column count");
  }

  if (normalizedValues.length === CSV_COLUMNS.length) {
    return normalizedValues;
  }

  return [
    ...normalizedValues,
    ...Array.from(
      { length: CSV_COLUMNS.length - normalizedValues.length },
      () => ""
    ),
  ];
}

function buildColorSnapshot(row: CsvRow) {
  const snapshot: Record<string, unknown> = {};

  for (const column of FABRIC_CSV_COLOR_COLUMNS) {
    const value = row[`color.${column}`];
    if (!value && !REQUIRED_COLOR_STRING_COLUMNS.has(column)) {
      continue;
    }

    if (
      column === "textWhite" ||
      column === "disabled" ||
      column === "colorEndOfLife"
    ) {
      snapshot[column] = parseBoolean(value);
      continue;
    }

    snapshot[column] = value;
  }

  if (typeof snapshot.name !== "string" || snapshot.name.trim().length === 0) {
    throw new Error("Color rows must include a color.name value");
  }

  return snapshot;
}

export function exportFabricToCsv(snapshot: FabricSpecsModelSnapshot) {
  const rows: CsvRow[] = [];
  const fabricRow = createEmptyRow("fabric");

  for (const column of FABRIC_CSV_ROOT_COLUMNS) {
    fabricRow[`fabric.${column}`] = readString(snapshot, column);
  }
  rows.push(fabricRow);

  if (snapshot.defaultColor && typeof snapshot.defaultColor === "object") {
    rows.push(
      buildColorRow(
        "defaultColor",
        snapshot.defaultColor as Record<string, unknown>
      )
    );
  }

  const colors = Array.isArray(snapshot.colors) ? snapshot.colors : [];
  for (const color of colors) {
    if (!color || typeof color !== "object") {
      continue;
    }
    rows.push(buildColorRow("color", color as Record<string, unknown>));
  }

  return Papa.unparse([CSV_COLUMNS, ...rows.map(serializeRow)], {
    delimiter: ",",
  });
}

export function importFabricFromCsv(input: string): FabricSpecsModelSnapshot {
  // Keep tabs intact so pasted TSV rows can omit trailing empty cells.
  const trimmedInput = input.replace(/^[\r\n]+/, "").replace(/[\r\n]+$/, "");
  const rows = parseDelimited(trimmedInput, detectDelimiter(trimmedInput));
  if (rows.length < 2) {
    throw new Error("CSV must include a header row and at least one data row");
  }

  const header = normalizeRowWidth(rows[0]);
  if (header.length !== CSV_COLUMNS.length) {
    throw new Error("CSV header does not match the expected fabric format");
  }

  for (const [index, column] of CSV_COLUMNS.entries()) {
    if (header[index] !== column) {
      throw new Error("CSV header does not match the expected fabric format");
    }
  }

  let fabricSnapshot: FabricSpecsModelSnapshot | null = null;
  let defaultColor: Record<string, unknown> | null = null;
  const colors: Record<string, unknown>[] = [];

  for (const rawValues of rows.slice(1)) {
    const values = normalizeRowWidth(rawValues);

    const row = CSV_COLUMNS.reduce(
      (result, column, index) => ({
        ...result,
        [column]: values[index] ?? "",
      }),
      {} as CsvRow
    );

    const recordType = row.recordType as CsvRecordType;
    if (recordType === "fabric") {
      if (fabricSnapshot) {
        throw new Error("CSV must include only one fabric row");
      }

      const name = row["fabric.name"].trim();
      if (!name) {
        throw new Error("Fabric row must include fabric.name");
      }

      fabricSnapshot = {
        name,
        colors: [],
      };

      for (const column of FABRIC_CSV_ROOT_COLUMNS) {
        const value = row[`fabric.${column}`].trim();
        if (column === "name") {
          continue;
        }
        if (value) {
          fabricSnapshot[column] = value;
        }
      }
      continue;
    }

    if (recordType === "defaultColor") {
      if (defaultColor) {
        throw new Error("CSV must include at most one defaultColor row");
      }
      defaultColor = buildColorSnapshot(row);
      continue;
    }

    if (recordType === "color") {
      colors.push(buildColorSnapshot(row));
      continue;
    }

    throw new Error(`Unsupported recordType "${row.recordType}"`);
  }

  if (!fabricSnapshot) {
    throw new Error("CSV must include one fabric row");
  }

  if (defaultColor) {
    fabricSnapshot.defaultColor = defaultColor;
  }
  fabricSnapshot.colors = colors;
  return fabricSnapshot;
}
